import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface RejectDialogProps {
  /** The decision being rejected, or null when the dialog is closed. */
  decision: { id: string; summary: string; skillName: string } | null
  busy: boolean
  onConfirm: (id: string, reason: string) => void
  onCancel: () => void
}

/**
 * Reason-capture modal for rejecting a decision. The reason is optional — Mark
 * can confirm with an empty box — but prompting for it turns a silent "no" into
 * feedback the originating agent (and the activity log) can learn from.
 *
 * Dependency-free modal: a fixed overlay + Escape-to-close + autofocus. No focus
 * trap library; the command centre is a single-operator tool and the modal has
 * only three focusable controls.
 */
export function RejectDialog({ decision, busy, onConfirm, onCancel }: RejectDialogProps) {
  const [reason, setReason] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Reset the field whenever a new decision opens the dialog.
  useEffect(() => {
    if (decision) {
      setReason('')
      // Defer focus until the element is mounted.
      const t = setTimeout(() => textareaRef.current?.focus(), 0)
      return () => clearTimeout(t)
    }
  }, [decision])

  useEffect(() => {
    if (!decision) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [decision, onCancel])

  if (!decision) return null

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 animate-in fade-in duration-150"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reject-dialog-title"
      onClick={onCancel}
    >
      <div
        className="relative w-full max-w-md bg-cmd-container p-5 shadow-xl animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-cmd-error" />
        <div className="flex items-start justify-between gap-3">
          <div className="pl-2">
            <h2 id="reject-dialog-title" className="font-sans text-sm font-semibold text-white">
              Reject this decision?
            </h2>
            <p className="mt-1 text-[0.8rem] leading-relaxed text-white/70">{decision.summary}</p>
            <p className="mt-1 font-mono text-[0.6rem] text-cmd-outline">{decision.skillName}</p>
          </div>
          <button
            onClick={onCancel}
            className="shrink-0 text-cmd-outline hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="mt-4 block pl-2">
          <span className="font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase">
            Why are you rejecting this? (optional)
          </span>
          <textarea
            ref={textareaRef}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Wrong tone, send a softer version instead…"
            className="mt-1.5 w-full resize-none bg-cmd-base p-2.5 text-sm text-white placeholder:text-cmd-outline-variant focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-cmd-error"
          />
        </label>

        <div className="mt-4 flex justify-end gap-2 pl-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={onCancel}
            disabled={busy}
            className="font-label text-xs tracking-wider"
          >
            CANCEL
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => onConfirm(decision.id, reason.trim())}
            disabled={busy}
            className="font-label text-xs tracking-wider"
          >
            {busy ? 'REJECTING…' : 'CONFIRM REJECT'}
          </Button>
        </div>
      </div>
    </div>
  )
}
